import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store';
import { useSSE } from '@/hooks/useSSE';
import { useDebugLogin } from '@/hooks/useAuth';
import { authApi } from '@/utils/api';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/Login';
import FeedPage from '@/pages/Feed';
import DigestsPage from '@/pages/Digests';
import LessonsPage from '@/pages/Lessons';
import SettingsPage from '@/pages/Settings';
import RSSManagerPage from '@/pages/RSSManager';
import RSSArticlesPage from '@/pages/RSSArticles';
import XAccountsPage from '@/pages/XAccounts';
import DebugPage from '@/pages/DebugPage';


interface DebugStatus {
  settings: {
    DEBUG: boolean;
    DEBUG_LOGIN: boolean;
    DEBUG_LOGIN_USERNAME: string | null;
  };
  environment: {
    DEBUG: boolean;
    DEBUG_LOGIN: boolean;
  };
  is_debug_login_active: boolean;
}

/**
 * AutoLoginWrapper: Check debug status first, then auto-login if enabled
 * - If backend DEBUG_LOGIN is enabled: auto-login and show app
 * - If backend DEBUG_LOGIN is disabled: show login page
 * - If already authenticated: just show app
 */
function AutoLoginWrapper({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, token } = useAuthStore();
  const debugLoginMutation = useDebugLogin();
  const [status, setStatus] = useState<DebugStatus | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  
  console.log('[AutoLoginWrapper] Render, isAuthenticated:', isAuthenticated, 'token:', token ? 'exists' : 'null');
  
  // Enable SSE when authenticated
  useSSE(isAuthenticated);
  
  useEffect(() => {
    console.log('[AutoLoginWrapper] useEffect triggered, isAuthenticated:', isAuthenticated);
    
    // First check debug status
    const checkDebugStatus = async () => {
      try {
        console.log('[AutoLoginWrapper] Fetching debug status...');
        const debugStatus = await authApi.getDebugStatus();
        console.log('[AutoLoginWrapper] Debug status:', debugStatus);
        setStatus(debugStatus);
        
        // Auto login if debug login is active and not authenticated
        if (debugStatus.is_debug_login_active && !isAuthenticated) {
          console.log('[AutoLoginWrapper] Debug login is active, calling mutate...');
          
          // Use direct fetch to debug
          try {
            const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8000/api/v1';
            const response = await fetch(`${API_BASE}/auth/debug-login`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            });
            console.log('[AutoLoginWrapper] Direct fetch response status:', response.status);
            const data = await response.json();
            console.log('[AutoLoginWrapper] Direct fetch response data:', data);
            
            if (response.ok && data.access_token) {
              console.log('[AutoLoginWrapper] Direct login success, setting auth...');
              const user = data.user || { username: 'debug_test', email: 'debug_test@example.com' };
              localStorage.setItem('token', data.access_token);
              useAuthStore.getState().setAuth(user, data.access_token);
            }
          } catch (fetchError) {
            console.error('[AutoLoginWrapper] Direct fetch error:', fetchError);
          }
        } else {
          console.log('[AutoLoginWrapper] Skipping auto login:', { 
            is_debug_login_active: debugStatus.is_debug_login_active, 
            isAuthenticated 
          });
          setIsChecking(false);
        }
      } catch (error) {
        console.error('[AutoLoginWrapper] Failed to get debug status:', error);
        setIsChecking(false);
      }
    };
    
    if (!isAuthenticated) {
      checkDebugStatus();
    } else {
      console.log('[AutoLoginWrapper] Already authenticated, skipping check');
      setIsChecking(false);
    }
  }, [isAuthenticated]);
  
  // If authenticated, show the app
  if (isAuthenticated) {
    return <>{children}</>;
  }
  
  // If checking status, show loading
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">正在连接服务器...</p>
        </div>
      </div>
    );
  }
  
  // If debug login is active but login failed, show error
  if (status?.is_debug_login_active && debugLoginMutation.isError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-destructive text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold mb-2">调试登录失败</h2>
          <p className="text-muted-foreground mb-4">
            后端 DEBUG_LOGIN 已启用，但自动登录失败。请刷新页面重试。
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            重试
          </button>
        </div>
      </div>
    );
  }
  
  // If debug login is active and pending, show loading
  if (status?.is_debug_login_active && debugLoginMutation.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">正在自动登录...</p>
        </div>
      </div>
    );
  }
  
  // Otherwise show login page
  return <LoginPage />;
}

function AppContent() {
  const { isAuthenticated } = useAuthStore();
  
  // Enable SSE when authenticated (only for already logged in users)
  useSSE(isAuthenticated);
  
  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" /> : <AutoLoginWrapper><Layout /></AutoLoginWrapper>
      } />
      <Route path="/" element={
        <AutoLoginWrapper>
          <Layout />
        </AutoLoginWrapper>
      }>
        <Route index element={<FeedPage />} />
        <Route path="digests" element={<DigestsPage />} />
        <Route path="lessons" element={<LessonsPage />} />
        <Route path="rss" element={<RSSManagerPage />} />
        <Route path="rss/articles" element={<RSSArticlesPage />} />
        <Route path="x-accounts" element={<XAccountsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="debug" element={<DebugPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return <AppContent />;
}

export default App;
